import { useState } from 'react';
import { Plus, Undo2 } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreateSalesReturnMutation, useDeletedSalesQuery, useSaleQuery, useSalesQuery, useSalesReturnActionMutation,
  useSalesReturnsQuery, type ReturnCondition, type ReturnStatus, type SalesReturn,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';

const STATUS_TONE: Record<ReturnStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  PENDING_INSPECTION: 'warning',
  APPROVED: 'brand',
  REJECTED: 'danger',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

const CONDITIONS: { value: ReturnCondition; label: string; note: string }[] = [
  { value: 'RESELLABLE', label: 'Resellable', note: 'Goes back into sellable stock.' },
  { value: 'DAMAGED', label: 'Damaged', note: 'Comes back, then written off.' },
  { value: 'EXPIRED', label: 'Expired', note: 'Comes back, then written off.' },
  { value: 'MISSING', label: 'Never arrived', note: 'Nothing is posted to stock.' },
];

interface ReturnLineDraft {
  saleItemId: string;
  quantity: string;
  condition: ReturnCondition;
}

export function ReturnsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useSalesReturnsQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    status: status || undefined,
  });

  // Only a completed sale can be returned against.
  const { data: sales } = useSalesQuery({ limit: 100, status: 'COMPLETED' });
  const [saleId, setSaleId] = useState('');
  const sale = useSaleQuery(saleId, { skip: !saleId });

  const [lines, setLines] = useState<Record<string, ReturnLineDraft>>({});
  const [restockingFee, setRestockingFee] = useState('');
  const [reason, setReason] = useState('');

  const [createReturn, createState] = useCreateSalesReturnMutation();
  const [runAction, actionState] = useSalesReturnActionMutation();

  const chosen = Object.values(lines).filter((line) => Number(line.quantity) > 0);

  const resetForm = (): void => {
    setSaleId('');
    setLines({});
    setRestockingFee('');
    setReason('');
    setFailure(null);
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await createReturn({
        saleId,
        reason: reason || undefined,
        restockingFee: restockingFee ? Number(restockingFee) : undefined,
        lines: chosen.map((line) => ({
          saleItemId: line.saleItemId,
          quantity: Number(line.quantity),
          condition: line.condition,
        })),
      }).unwrap();

      setCreating(false);
      resetForm();
    } catch (createError) {
      setFailure(apiErrorMessage(createError, 'That return could not be raised'));
    }
  };

  const act = async (id: string, action: string, body?: Record<string, unknown>): Promise<void> => {
    setFailure(null);
    try {
      await runAction({ id, action, body }).unwrap();
    } catch (actionError) {
      setFailure(apiErrorMessage(actionError, `That return could not be ${action}ed`));
    }
  };

  return (
    <>
      <PageHeader
        title="Returns"
        description="Goods coming back. Stock and credit move only when a return is approved."
        action={
          <Can permission={P.SALE_REFUND}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New return</Button>
          </Can>
        }
      />

      {failure && (
        <div className="mb-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Returns', value: String(data.summary.count), hint: 'in this period' },
            {
              label: 'Value returned',
              value: formatMoney(String(data.summary.returned), format),
              hint: 'goods sent back',
            },
            {
              label: 'Refunded',
              value: formatMoney(String(data.summary.refunded), format),
              hint: 'money given back',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="w-full sm:w-56">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING_INSPECTION">Awaiting inspection</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED">Rejected</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Undo2}
            title={status ? 'Nothing with that status' : 'No returns yet'}
            description="Raise a return against a completed sale."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Sale</Th>
                  <Th>Customer</Th>
                  <Th>Date</Th>
                  <Th numeric>Credit</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((entry: SalesReturn) => (
                  <tr key={entry.id} className="hover:bg-sunken">
                    <Td className="font-medium">{entry.number}</Td>
                    <Td className="text-secondary">{entry.sale?.number ?? '—'}</Td>
                    <Td className="text-secondary">{entry.customer?.name ?? 'Walk-in'}</Td>
                    <Td className="text-secondary">{formatDate(entry.returnedAt, format)}</Td>
                    <Td numeric>{formatMoney(entry.total, format)}</Td>
                    <Td>
                      <Badge tone={STATUS_TONE[entry.status]}>
                        {entry.status.toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      {(entry.status === 'DRAFT' || entry.status === 'PENDING_INSPECTION') && (
                        <Can permission={P.SALE_REFUND}>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() =>
                              void act(entry.id, 'reject', { reason: 'Rejected on inspection' })
                            }
                          >
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() => void act(entry.id, 'approve')}
                          >
                            Approve
                          </Button>
                        </Can>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Can permission={P.SALE_DELETE}>
        <DeletedReceipts />
      </Can>

      <Modal
        open={creating}
        onClose={() => {
          setCreating(false);
          resetForm();
        }}
        title="New return"
        description="Pick the sale, then say how much of each line came back and in what condition."
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={!saleId || chosen.length === 0}
            >
              Raise return
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}

          <Select
            label="Sale"
            required
            value={saleId}
            onChange={(event) => {
              setSaleId(event.target.value);
              setLines({});
            }}
            hint="Only completed sales can be returned against."
          >
            <option value="">Select a sale</option>
            {sales?.items.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.number} — {entry.customer?.name ?? 'Walk-in'} —{' '}
                {formatMoney(entry.total, format)}
              </option>
            ))}
          </Select>

          {sale.data?.items && (
            <div className="space-y-3">
              <p className="text-sm font-medium">What came back</p>
              {sale.data.items.map((item) => {
                const remaining = Number(item.quantity) - Number(item.returnedQty);
                const draft = lines[item.id];
                return (
                  <div key={item.id} className="flex flex-wrap items-end gap-3">
                    <div className="min-w-44 flex-1">
                      <p className="text-sm">{item.description}</p>
                      <p className="text-xs text-muted">
                        {formatQuantity(String(remaining), format)} of{' '}
                        {formatQuantity(item.quantity, format)} still returnable
                      </p>
                    </div>
                    <div className="w-24">
                      <Input
                        label="Qty"
                        type="number"
                        min="0"
                        max={remaining}
                        step="0.0001"
                        value={draft?.quantity ?? ''}
                        onChange={(event) =>
                          setLines({
                            ...lines,
                            [item.id]: {
                              saleItemId: item.id,
                              quantity: event.target.value,
                              condition: draft?.condition ?? 'RESELLABLE',
                            },
                          })
                        }
                      />
                    </div>
                    <div className="w-44">
                      <Select
                        label="Condition"
                        value={draft?.condition ?? 'RESELLABLE'}
                        onChange={(event) =>
                          setLines({
                            ...lines,
                            [item.id]: {
                              saleItemId: item.id,
                              quantity: draft?.quantity ?? '',
                              condition: event.target.value as ReturnCondition,
                            },
                          })
                        }
                        hint={
                          CONDITIONS.find((c) => c.value === (draft?.condition ?? 'RESELLABLE'))
                            ?.note
                        }
                      >
                        {CONDITIONS.map((condition) => (
                          <option key={condition.value} value={condition.value}>
                            {condition.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Restocking fee"
              type="number"
              min="0"
              step="0.01"
              value={restockingFee}
              onChange={(event) => setRestockingFee(event.target.value)}
              hint="Withheld from the credit."
            />
            <Input
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * Deleted receipts are returns too: deleting one put its goods back on the
 * shelf and took its money back out of the account. They are listed here so
 * the Returns screen shows every way goods and money came back.
 */
function DeletedReceipts() {
  const format = useFormat();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, refetch } = useDeletedSalesQuery({ page, limit: 10 });

  return (
    <Card className="mt-6">
      <div className="border-b border-subtle p-4">
        <h2 className="text-sm font-semibold">Deleted receipts</h2>
        <p className="text-xs text-muted">
          Deleting a receipt returns its goods to stock and takes its payments back out of the
          account they were paid into.
        </p>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState icon={Undo2} title="No receipts have been deleted" />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Receipt</Th>
                <Th>Deleted</Th>
                <Th>Back in stock</Th>
                <Th className="text-right">Refunded</Th>
                <Th>Reason</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((sale) => (
                <tr key={sale.id}>
                  <Td>
                    <p className="font-medium">{sale.number}</p>
                    <p className="text-xs text-muted">
                      {sale.customer?.name ?? 'Walk-in'} · {formatMoney(sale.total, format)}
                    </p>
                  </Td>
                  <Td>
                    <p>{formatDate(sale.deletedAt, format)}</p>
                    <p className="text-xs text-muted">{sale.deletedBy ?? 'Unknown'}</p>
                  </Td>
                  <Td>
                    {sale.restocked.filter((line) => Number(line.quantity) > 0).length === 0 ? (
                      <span className="text-muted">Nothing — it never took stock</span>
                    ) : (
                      <ul className="text-xs">
                        {sale.restocked
                          .filter((line) => Number(line.quantity) > 0)
                          .map((line, index) => (
                            <li key={index}>{line.description} × {formatQuantity(line.quantity)}</li>
                          ))}
                      </ul>
                    )}
                  </Td>
                  <Td className="text-right">
                    <p className="font-medium">{formatMoney(sale.refunded, format)}</p>
                    {sale.refunds.map((refund) => (
                      <p key={refund.payment} className="text-xs text-muted">
                        from {refund.account ?? refund.method ?? 'no account'}
                      </p>
                    ))}
                  </Td>
                  <Td className="text-xs">{sale.reason ?? <span className="text-muted">None given</span>}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination
            page={data.meta.page}
            totalPages={data.meta.totalPages}
            total={data.meta.total}
            onPageChange={setPage}
          />
        </>
      )}
    </Card>
  );
}
